"use client";

import Link from "next/link";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface Metric {
  value: string;
  label: string;
  icon: LucideIcon;
  accent: string;
}

export interface ExecutiveSummarySectionProps {
  id?: string;
  eyebrow: string;
  title: string;
  intro: string;
  paragraphs: string[];
  metrics?: Metric[];
  cta: { label: string; href: string };
  primaryAccent?: string;
}

export function ExecutiveSummarySection({
  id = "executive-summary",
  eyebrow,
  title,
  intro,
  paragraphs,
  metrics = [],
  cta,
  primaryAccent = "var(--color-purple)",
}: ExecutiveSummarySectionProps) {
  return (
    <Section id={id} className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute top-1/3 left-1/4 h-80 w-80 rounded-full opacity-15 blur-[120px]"
          style={{ background: primaryAccent }}
        />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny" style={{ color: primaryAccent }}>
              {eyebrow}
            </span>
            <h2 className="mt-3">{title}</h2>
          </div>
        </Reveal>

        <div className="mt-10 max-w-4xl space-y-5">
          <Reveal>
            <p className="text-lg font-medium text-[rgb(var(--fg))] leading-snug">
              {intro}
            </p>
          </Reveal>
          {paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 60 + 100}>
              <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))]">{p}</p>
            </Reveal>
          ))}
        </div>

        {metrics.length > 0 && (
          <Reveal delay={300}>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 max-w-5xl">
              {metrics.map((m) => {
                const Icon = m.icon;
                return (
                  <div
                    key={m.label}
                    className="glass rounded-2xl p-5"
                    style={{
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${m.accent} 12%, transparent)`,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-4 w-4" style={{ color: m.accent }} />
                    </div>
                    <div className="text-2xl font-bold" style={{ color: m.accent }}>
                      {m.value}
                    </div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[rgb(var(--fg-subtle))] leading-tight mt-1">
                      {m.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        )}

        <Reveal delay={400}>
          <div className="mt-10">
            <Link
              href={cta.href}
              className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
            >
              {cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
